import React from "react";
import { X, PlusCircle, FileText, Loader2, ArrowRight, User } from "lucide-react";

export default function SelectOpenBillModal({
  isOpen,
  onClose,
  cartItemsCount,
  pendingOrders = [],
  loadingOrders = false,
  salesTypes = [],
  onSaveNewBill,
  onMergeToExistingBill,
}) {
  if (!isOpen) return null;

  const getOrderFinancialDetails = (order) => {
    const orderTypeId = String(order.order_type_id || "");
    const sellerTypeSlug = String(order.seller_type || "").toUpperCase();

    const matchedType = salesTypes.find((t) => {
      const tId = String(t.id || "");
      const tName = String(t.name || "").toUpperCase();
      const tSlug = String(t.slug || t.code || t.type || "").toUpperCase();
      return (
        (tId && orderTypeId && tId === orderTypeId) ||
        (tSlug && sellerTypeSlug && tSlug === sellerTypeSlug) ||
        (tName && sellerTypeSlug && tName === sellerTypeSlug)
      );
    });

    let discountVal = Number(order.discount_total || order.discount || 0);
    let markupVal = Number(order.additional_costs || order.additional_cost || 0);
    let grandTotal = Number(order.grand_total || order.total || 0);

    let itemsSubtotal = Number(order.subtotal || order.sub_total || 0);
    if (!itemsSubtotal && Array.isArray(order.items) && order.items.length > 0) {
      itemsSubtotal = order.items.reduce(
        (sum, item) => sum + Number(item.total_price ?? (Number(item.quantity || item.qty || 1) * Number(item.unit_price || item.price || 0))),
        0
      );
    }

    if (!itemsSubtotal) {
      itemsSubtotal = grandTotal + discountVal - markupVal;
    }

    if (discountVal === 0 && matchedType && matchedType.jenis === "Diskon") {
      const numNilai = Number(matchedType.nilai) || 0;
      discountVal = matchedType.satuan === "%" ? (itemsSubtotal * numNilai) / 100 : numNilai;
      grandTotal = Math.max(itemsSubtotal - discountVal, 0);
    }

    if (markupVal === 0 && matchedType && matchedType.jenis === "Markup") {
      const numNilai = Number(matchedType.nilai) || 0;
      markupVal = matchedType.satuan === "%" ? (itemsSubtotal * numNilai) / 100 : numNilai;
      grandTotal = itemsSubtotal + markupVal;
    }

    const discountPercent =
      discountVal > 0 && itemsSubtotal > 0
        ? Math.round((discountVal / itemsSubtotal) * 100)
        : matchedType && matchedType.jenis === "Diskon" && matchedType.satuan === "%"
        ? Number(matchedType.nilai) || 0
        : 0;

    const markupPercent =
      markupVal > 0 && itemsSubtotal > 0
        ? Math.round((markupVal / itemsSubtotal) * 100)
        : matchedType && matchedType.jenis === "Markup" && matchedType.satuan === "%"
        ? Number(matchedType.nilai) || 0
        : 0;

    return {
      originalPrice: itemsSubtotal,
      grandTotal,
      discountVal,
      markupVal,
      discountPercent,
      markupPercent,
      hasDiscount: discountVal > 0,
      hasMarkup: markupVal > 0,
    };
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh] modal-panel-enter">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div>
            <h3 className="font-bold text-slate-800 text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Simpan / Tambah ke Open Bill
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Pilih apakah ingin membuat bill baru atau menambahkan {cartItemsCount} item di keranjang ke bill yang sudah ada.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-200/60 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {/* Option 1: Create New Bill */}
          <button
            onClick={onSaveNewBill}
            className="w-full p-4 rounded-xl border-2 border-teal-500 bg-teal-50/40 hover:bg-teal-50 text-left transition-all group flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold shrink-0 shadow-xs">
                <PlusCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-teal-950 group-hover:text-teal-700 transition-colors">
                  Buat Open Bill Baru
                </h4>
                <p className="text-xs text-teal-700/80 mt-0.5">
                  Simpan pesanan keranjang saat ini sebagai transaksi pending baru
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-teal-600 group-hover:translate-x-1 transition-transform shrink-0" />
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <div className="h-px bg-slate-200 flex-1"></div>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Atau Gabung Ke Bill Aktif
            </span>
            <div className="h-px bg-slate-200 flex-1"></div>
          </div>

          {/* Option 2: Merge into existing Open Bill */}
          <div className="space-y-2">
            {loadingOrders && (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-500 text-xs">
                <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                Memuat daftar Open Bill aktif...
              </div>
            )}

            {!loadingOrders && pendingOrders.length === 0 && (
              <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <p className="text-xs text-slate-500 font-medium">
                  Belum ada Open Bill aktif saat ini.
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Klik "Buat Open Bill Baru" di atas untuk menyimpan.
                </p>
              </div>
            )}

            {!loadingOrders &&
              pendingOrders.map((order) => {
                const fin = getOrderFinancialDetails(order);
                const itemCount = (order.items || []).reduce((acc, item) => acc + Number(item.quantity || 1), 0);

                return (
                  <div
                    key={order.id}
                    className="p-3.5 rounded-xl border border-slate-200 hover:border-teal-400 bg-white hover:bg-slate-50/50 transition-all flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-xs text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                          {order.transaction_number || order.id?.slice(0, 8)}
                        </span>
                        <span className="text-xs text-slate-500 font-medium flex items-center gap-1 truncate">
                          <User className="w-3 h-3 text-slate-400 shrink-0" />
                          {order.customer_name || "Pelanggan Umum"}
                        </span>
                        {order.seller_type && (
                          <span className="text-[10px] font-semibold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded">
                            {order.seller_type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                        <span>{itemCount} Item</span>
                        <span>•</span>
                        <span className="flex items-center gap-1.5">
                          <span className="font-bold text-slate-900">
                            Rp {fin.grandTotal.toLocaleString("id-ID")}
                          </span>
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => onMergeToExistingBill(order)}
                      className="px-3 py-1.5 bg-slate-900 hover:bg-teal-600 text-white text-xs font-bold rounded-lg transition-colors shadow-xs shrink-0 flex items-center gap-1.5">
                      <PlusCircle className="w-3.5 h-3.5" />
                      Gabung Ke Bill Ini
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
